import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, LoadingState, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, CheckCircle, WarningCircle, ClockClockwise, Wrench, Phone, EnvelopeSimple,
    ArrowsClockwise, ChatCircleDots, Lock, LockKey, Timer, User, X, Camera, ImageSquare, Trash,
    Buildings, HouseLine,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
const CATEGORIES = [
    "plumbing", "electrical", "civil_structural", "lift_elevator", "water_supply",
    "drainage_sewage", "housekeeping", "security", "common_area", "parking",
    "gardening", "pest_control", "internet_dtv", "generator_power_backup",
    "fire_safety", "amenities", "staff_service", "other",
];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["new", "acknowledged", "assigned", "in_progress", "on_hold", "resolved", "closed", "reopened"];
const STATUS_TARGETS_STAFF = ["acknowledged", "assigned", "in_progress", "on_hold", "resolved", "closed", "reopened"];

const ALLOWED_IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp"];
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_MB = 8;

const catLabel = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, s => s.toUpperCase());
const statusLabel = (s) => (s || "new").replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());

const STATUS_VARIANT = {
    new: "warn", acknowledged: "default", assigned: "default",
    in_progress: "default", on_hold: "warn", resolved: "success",
    closed: "neutral", reopened: "danger",
};

const PRIORITY_VARIANT = { low: "neutral", medium: "default", high: "warn", urgent: "danger" };
const AGE_VARIANT = { "0-2": "success", "3-7": "default", "8-15": "warn", "16+": "danger" };

// Sensible priority quick-suggestion for residents
const RESIDENT_PRIORITY_OPTIONS = ["low", "medium", "high"]; // 'urgent' downgraded to 'high' server-side

function fmt(dt) {
    if (!dt) return "—";
    try {
        const d = new Date(dt);
        return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return dt; }
}

function fmtDateOnly(dt) {
    if (!dt) return "—";
    try {
        const d = new Date(dt);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return dt; }
}

// ---------- Timeline Row ----------
function TimelineRow({ ev, isLast }) {
    const isInternal = ev.is_internal;
    const label = (() => {
        switch (ev.action) {
            case "created": return "Complaint created";
            case "status_changed": return `Status changed: ${statusLabel(ev.old_value)} → ${statusLabel(ev.new_value)}`;
            case "assigned": return `Assigned to ${ev.new_value?.name || "—"}${ev.new_value?.vendor_org ? ` (${ev.new_value.vendor_org})` : ""}`;
            case "resolved": return "Marked as resolved";
            case "resolution_confirmed": return "Resident confirmed resolution → Closed";
            case "reopened": return "Complaint reopened";
            case "category_changed": return `Category: ${catLabel(ev.old_value)} → ${catLabel(ev.new_value)}`;
            case "priority_changed": return `Priority: ${statusLabel(ev.old_value)} → ${statusLabel(ev.new_value)}`;
            case "subject_updated": return "Subject updated";
            case "comment_added": return "Public update";
            case "internal_note_added": return "Internal note";
            default: return ev.action;
        }
    })();
    return (
        <li className="relative pl-6" data-testid={`fr-tl-${ev.id}`}>
            <span className={`absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ${isInternal ? "bg-brand-action" : "bg-brand-ink"}`} />
            {!isLast && <span className="absolute left-1 top-4 bottom-0 w-px bg-brand-line" />}
            <div className="pb-4">
                <p className="text-xs text-brand-inkSoft">{fmt(ev.at)} · <span className="font-medium text-brand-ink">{ev.user_name || "System"}</span>{isInternal && <span className="ml-1.5 inline-flex items-center gap-1 text-brand-action"><LockKey size={11} weight="fill" /> Internal</span>}</p>
                <p className="text-sm text-brand-ink font-medium mt-0.5">{label}</p>
                {ev.comment && <p className="text-sm text-brand-inkSoft mt-1 italic">"{ev.comment}"</p>}
            </div>
        </li>
    );
}

// ---------- Attachment Thumb (protected fetch → blob URL) ----------
function AttachmentThumb({ complaintId, att }) {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        let revoked = null;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/complaints/${complaintId}/attachments/${att.file_id}`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revoked = url;
                setSrc(url);
            } catch (e) { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
    }, [complaintId, att.file_id]);
    if (err) return <div className="border border-dashed border-brand-line rounded-sm h-20 flex items-center justify-center text-[10px] text-brand-inkSoft">unavailable</div>;
    return (
        <a href={src || "#"} target="_blank" rel="noreferrer" data-testid={`complaint-attach-thumb-${att.file_id}`}
           className="block border border-brand-line rounded-sm overflow-hidden hover:border-brand-ink transition-colors">
            {src ? <img src={src} alt={att.file_name || "photo"} className="w-full h-20 object-cover" /> : <div className="h-20 bg-brand-bg animate-pulse" />}
        </a>
    );
}

// ---------- Detail Dialog ----------
function ComplaintDetail({ complaint, isStaff, user, staffList, onClose, onChange }) {
    const [detail, setDetail] = useState(complaint);
    const [timeline, setTimeline] = useState([]);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [commentBody, setCommentBody] = useState("");
    const [commentKind, setCommentKind] = useState("public");
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolutionSummary, setResolutionSummary] = useState("");
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenReason, setReopenReason] = useState("");

    async function loadAll() {
        setLoading(true);
        try {
            const [d, t, c] = await Promise.all([
                api.get(`/complaints/${complaint.id}`),
                api.get(`/complaints/${complaint.id}/timeline`),
                api.get(`/complaints/${complaint.id}/comments`),
            ]);
            setDetail(d.data); setTimeline(t.data); setComments(c.data);
        } catch (e) { toast.error(formatError(e)); onClose?.(); }
        finally { setLoading(false); }
    }
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [complaint.id]);

    async function changeStatus(newStatus, extraPromptLabel) {
        let comment = null;
        if (newStatus === "on_hold") {
            comment = window.prompt("Reason for putting on hold (optional):") || null;
        } else if (extraPromptLabel) {
            comment = window.prompt(extraPromptLabel) || null;
        }
        try {
            await api.post(`/complaints/${complaint.id}/status`, { status: newStatus, comment });
            toast.success(`Status → ${statusLabel(newStatus)}`);
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function assign(staffId) {
        try {
            await api.post(`/complaints/${complaint.id}/assign`, { assignee_type: "staff", assignee_id: staffId });
            toast.success("Assigned");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function changeCategory(cat) {
        try {
            await api.patch(`/complaints/${complaint.id}`, { category: cat });
            toast.success("Category updated");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function changePriority(pri) {
        try {
            await api.patch(`/complaints/${complaint.id}`, { priority: pri });
            toast.success("Priority updated");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function submitResolve() {
        if (!resolutionSummary.trim()) { toast.error("Resolution summary is required"); return; }
        try {
            await api.post(`/complaints/${complaint.id}/resolve`, { resolution_summary: resolutionSummary });
            toast.success("Marked as resolved");
            setResolveOpen(false); setResolutionSummary("");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function confirmResolution() {
        try {
            await api.post(`/complaints/${complaint.id}/confirm-resolution`);
            toast.success("Complaint closed — thanks for confirming!");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function submitReopen() {
        try {
            await api.post(`/complaints/${complaint.id}/reopen`, { reason: reopenReason || null });
            toast.success("Complaint reopened");
            setReopenOpen(false); setReopenReason("");
            await loadAll();
            onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function postComment() {
        if (!commentBody.trim()) return;
        try {
            await api.post(`/complaints/${complaint.id}/comments`, { body: commentBody, kind: commentKind });
            setCommentBody("");
            toast.success(commentKind === "internal" ? "Internal note added" : "Comment posted");
            await loadAll();
        } catch (e) { toast.error(formatError(e)); }
    }

    if (loading) return <LoadingState message="Loading complaint..." />;

    const status = detail.status;
    const canResidentConfirm = !isStaff && status === "resolved";
    const canResidentReopen = !isStaff && (status === "resolved" || status === "closed");

    return (
        <div className="space-y-6" data-testid="complaint-detail-dialog">
            {/* Header */}
            <div className="border-b border-brand-line pb-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft" data-testid="complaint-detail-number">{detail.complaint_number}</p>
                        <h2 className="font-heading text-xl md:text-2xl tracking-tight text-brand-ink mt-1" data-testid="complaint-detail-subject">{detail.subject || detail.title}</h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Chip variant={PRIORITY_VARIANT[detail.priority] || "default"} data-testid="complaint-detail-priority">{statusLabel(detail.priority)} priority</Chip>
                        <Chip variant={STATUS_VARIANT[status] || "default"} data-testid="complaint-detail-status">{statusLabel(status)}</Chip>
                        <Chip variant={AGE_VARIANT[detail.age_bucket] || "neutral"}><Timer size={11} weight="duotone" className="mr-1" />{detail.age_days}d old</Chip>
                    </div>
                </div>
            </div>

            {/* Info grid */}
            <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Category</span><span className="font-medium text-brand-ink">{catLabel(detail.category)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Raised by</span><span className="font-medium text-brand-ink">{detail.created_by_name}{detail.created_by_role && detail.created_by_role !== "resident" ? ` (${statusLabel(detail.created_by_role)})` : ""}</span></div>
                    <div className="flex justify-between" data-testid="complaint-detail-raised-for">
                        <span className="text-brand-inkSoft">Raised for</span>
                        <span className="font-medium text-brand-ink">
                            {detail.target_type === "COMMON_AREA" ? (
                                <><Buildings size={11} weight="duotone" className="inline mr-1" />Common Area</>
                            ) : detail.target_type === "RESIDENT" ? (
                                <><HouseLine size={11} weight="duotone" className="inline mr-1" />Resident</>
                            ) : "—"}
                        </span>
                    </div>
                    {detail.target_type === "COMMON_AREA" ? (
                        <>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Common area</span><span className="font-medium text-brand-ink">{detail.common_area || "—"}</span></div>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Location</span><span className="font-medium text-brand-ink">{detail.common_area_location || detail.location || "—"}</span></div>
                        </>
                    ) : (
                        <>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Resident</span><span className="font-medium text-brand-ink">{detail.target_resident_name || detail.created_by_name || "—"}</span></div>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Flat</span><span className="font-medium text-brand-ink">{detail.target_flat_label || detail.flat?.label || "—"}</span></div>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Block</span><span className="font-medium text-brand-ink">{detail.target_block_id || detail.flat?.block || "—"}</span></div>
                            <div className="flex justify-between"><span className="text-brand-inkSoft">Location</span><span className="font-medium text-brand-ink">{detail.location || "—"}</span></div>
                        </>
                    )}
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Created</span><span className="font-medium text-brand-ink">{fmt(detail.created_at)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Last update</span><span className="font-medium text-brand-ink">{fmt(detail.updated_at)}</span></div>
                    {detail.acknowledged_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Acknowledged</span><span className="font-medium text-brand-ink">{fmt(detail.acknowledged_at)}</span></div>}
                    {detail.assigned_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Assigned</span><span className="font-medium text-brand-ink">{fmt(detail.assigned_at)}</span></div>}
                    {detail.resolved_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Resolved</span><span className="font-medium text-brand-ink">{fmt(detail.resolved_at)}</span></div>}
                    {detail.closed_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Closed</span><span className="font-medium text-brand-ink">{fmt(detail.closed_at)}</span></div>}
                </div>
            </div>

            {/* Description */}
            <div className="bg-brand-bg border border-brand-line rounded-sm p-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Description</p>
                <p className="text-sm text-brand-ink whitespace-pre-wrap">{detail.description}</p>
            </div>

            {/* Attachments */}
            {detail.attachments && detail.attachments.length > 0 && (
                <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="complaint-detail-attachments">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1">
                        <ImageSquare size={12} weight="duotone" /> Attachments ({detail.attachments.length})
                    </p>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {detail.attachments.map(att => (
                            <AttachmentThumb key={att.id} complaintId={detail.id} att={att} />
                        ))}
                    </div>
                </div>
            )}

            {/* Assigned to */}
            {detail.assigned_staff && (
                <div className="bg-brand-sage/40 border border-brand-line rounded-sm p-4" data-testid="complaint-detail-assigned">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">
                        Assigned {detail.assigned_staff.assignee_type === "vendor" ? "vendor" : "staff"}
                    </p>
                    <p className="font-medium text-brand-ink">{detail.assigned_staff.name}</p>
                    <p className="text-xs text-brand-inkSoft">
                        {detail.assigned_staff.role_label}{detail.assigned_staff.vendor_org ? ` · ${detail.assigned_staff.vendor_org}` : ""}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2">
                        {detail.assigned_staff.phone && (
                            <a href={`tel:${detail.assigned_staff.phone}`} className="text-xs text-brand-action hover:underline flex items-center gap-1">
                                <Phone size={12} weight="duotone" /> {detail.assigned_staff.phone}
                            </a>
                        )}
                        {detail.assigned_staff.email && (
                            <a href={`mailto:${detail.assigned_staff.email}`} className="text-xs text-brand-action hover:underline flex items-center gap-1">
                                <EnvelopeSimple size={12} weight="duotone" /> Email
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Resolution card */}
            {detail.resolution_summary && (
                <div className="bg-[#ECFDF5]/40 border border-[#059669]/20 rounded-sm p-4" data-testid="complaint-detail-resolution">
                    <p className="text-[10px] uppercase tracking-overline text-[#059669] mb-1 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Resolution</p>
                    <p className="text-sm text-brand-ink whitespace-pre-wrap">{detail.resolution_summary}</p>
                    {detail.resolution_notes && <p className="text-xs text-brand-inkSoft mt-2 italic">Notes: {detail.resolution_notes}</p>}
                </div>
            )}

            {/* Resident confirm/reopen actions */}
            {canResidentConfirm && (
                <div className="bg-white border-2 border-brand-action rounded-sm p-4">
                    <p className="font-medium text-brand-ink">Has your issue been resolved?</p>
                    <p className="text-xs text-brand-inkSoft mb-3">Please confirm to close this complaint or reopen if it's not fixed.</p>
                    <div className="flex gap-2 flex-wrap">
                        <Button data-testid="complaint-confirm-resolution-btn" onClick={confirmResolution} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white">
                            <CheckCircle size={16} className="mr-1.5" /> Confirm Resolution
                        </Button>
                        <Button data-testid="complaint-reopen-btn" variant="outline" onClick={() => setReopenOpen(true)} className="rounded-full border-brand-action text-brand-action hover:bg-brand-action hover:text-white">
                            <ArrowsClockwise size={16} className="mr-1.5" /> Issue Not Resolved
                        </Button>
                    </div>
                </div>
            )}
            {canResidentReopen && !canResidentConfirm && (
                <Button data-testid="complaint-reopen-btn" variant="outline" onClick={() => setReopenOpen(true)} className="rounded-full border-brand-action text-brand-action hover:bg-brand-action hover:text-white">
                    <ArrowsClockwise size={16} className="mr-1.5" /> Reopen
                </Button>
            )}

            {/* Staff actions */}
            {isStaff && (
                <div className="bg-white border border-brand-line rounded-sm p-4 space-y-3" data-testid="complaint-staff-actions">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Staff Actions</p>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Category</Label>
                            <Select value={detail.category} onValueChange={changeCategory}>
                                <SelectTrigger data-testid="complaint-cat-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Priority</Label>
                            <Select value={detail.priority} onValueChange={changePriority}>
                                <SelectTrigger data-testid="complaint-pri-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Assign to</Label>
                            <Select value={detail.assigned_to || ""} onValueChange={assign}>
                                <SelectTrigger data-testid="complaint-assign-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick staff/vendor" /></SelectTrigger>
                                <SelectContent>{staffList.filter(s => s.is_active).map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name} · {catLabel(s.category)}{s.vendor_org ? ` (${s.vendor_org})` : ""}</SelectItem>
                                ))}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Status</Label>
                            <Select value={status} onValueChange={(v) => v !== status && changeStatus(v)}>
                                <SelectTrigger data-testid="complaint-status-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUS_TARGETS_STAFF.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    {status !== "resolved" && status !== "closed" && (
                        <Button data-testid="complaint-resolve-btn" onClick={() => setResolveOpen(true)} className="w-full md:w-auto rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            <CheckCircle size={16} className="mr-1.5" /> Mark as Resolved
                        </Button>
                    )}
                </div>
            )}

            {/* Comments */}
            <div className="border-t border-brand-line pt-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1"><ChatCircleDots size={12} weight="duotone" /> Comments & Updates</p>
                <div className="space-y-3 mb-4" data-testid="complaint-comments">
                    {comments.length === 0 && <p className="text-xs text-brand-inkSoft italic">No comments yet.</p>}
                    {comments.map(c => (
                        <div key={c.id} className={`rounded-sm p-3 ${c.kind === "internal" ? "bg-brand-action/10 border border-brand-action/30" : "bg-brand-bg border border-brand-line"}`} data-testid={`complaint-comment-${c.id}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-brand-ink flex items-center gap-1">
                                    <User size={12} /> {c.user_name}
                                    {c.kind === "internal" && <span className="ml-1 inline-flex items-center gap-1 text-brand-action text-[10px]"><LockKey size={10} weight="fill" /> INTERNAL</span>}
                                </p>
                                <p className="text-[10px] text-brand-inkSoft">{fmt(c.at)}</p>
                            </div>
                            <p className="text-sm text-brand-ink mt-1 whitespace-pre-wrap">{c.body}</p>
                        </div>
                    ))}
                </div>
                <div className="space-y-2">
                    <Textarea data-testid="complaint-comment-input" rows={2} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Write an update..." className="rounded-sm border-brand-line" />
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        {isStaff && (
                            <Select value={commentKind} onValueChange={setCommentKind}>
                                <SelectTrigger data-testid="complaint-comment-kind" className="h-9 rounded-sm border-brand-line w-40"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="public">Public update</SelectItem>
                                    <SelectItem value="internal">Internal note</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        <Button data-testid="complaint-post-comment-btn" onClick={postComment} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white ml-auto">Post</Button>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="border-t border-brand-line pt-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1"><ClockClockwise size={12} weight="duotone" /> Activity Timeline</p>
                <ul className="space-y-0" data-testid="complaint-timeline">
                    {timeline.map((ev, i) => <TimelineRow key={ev.id} ev={ev} isLast={i === timeline.length - 1} />)}
                </ul>
            </div>

            {/* Resolve modal */}
            <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl tracking-tight">Mark as Resolved</DialogTitle>
                        <DialogDescription>A resolution summary is required. This will be visible to the resident.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>Resolution Summary</Label>
                        <Textarea data-testid="complaint-resolution-summary" rows={3} value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)} placeholder="e.g. Tap cartridge replaced. Leakage stopped." className="rounded-sm border-brand-line" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResolveOpen(false)} className="rounded-full">Cancel</Button>
                        <Button data-testid="complaint-resolve-submit" onClick={submitResolve} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Confirm Resolved</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reopen modal */}
            <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl tracking-tight">Reopen Complaint</DialogTitle>
                        <DialogDescription>Let the committee know what's still unresolved.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>Reason (optional)</Label>
                        <Textarea data-testid="complaint-reopen-reason" rows={2} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="e.g. Leakage has started again." className="rounded-sm border-brand-line" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReopenOpen(false)} className="rounded-full">Cancel</Button>
                        <Button data-testid="complaint-reopen-submit" onClick={submitReopen} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Reopen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- Main Page ----------
export default function Complaints() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const [complaints, setComplaints] = useState([]);
    const [staff, setStaff] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // Master data for admin: flats + common areas
    const [flats, setFlats] = useState([]);
    const [commonAreas, setCommonAreas] = useState([]);

    const emptyForm = () => ({
        subject: "", description: "", category: "plumbing", priority: "medium", location: "",
        target_type: isStaff ? "RESIDENT" : "RESIDENT",   // admin default: resident
        target_block_id: "",
        target_flat_id: "",
        common_area: "",
        common_area_location: "",
    });
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [pendingPhotos, setPendingPhotos] = useState([]);   // [{ file, previewUrl, uploading, file_id }]

    // Filters
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [targetTypeFilter, setTargetTypeFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [selected, setSelected] = useState(null);

    async function loadList(overrideQ) {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter && statusFilter !== "all") params.status = statusFilter === "open_all" ? "open_all" : statusFilter;
            if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
            if (priorityFilter && priorityFilter !== "all") params.priority = priorityFilter;
            if (targetTypeFilter && targetTypeFilter !== "all") params.target_type = targetTypeFilter;
            const qVal = overrideQ !== undefined ? overrideQ : q;
            if (qVal.trim()) params.q = qVal.trim();
            if (sortBy) params.sort = sortBy;
            const [c, s] = await Promise.all([
                api.get("/complaints", { params }),
                isStaff ? api.get("/staff") : Promise.resolve({ data: [] }),
            ]);
            setComplaints(c.data);
            setStaff(s.data);
            if (isStaff) {
                try { const sm = await api.get("/complaints/summary"); setSummary(sm.data); } catch (e) { /* ignore */ }
            }
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter, priorityFilter, targetTypeFilter, sortBy]);

    // Load master data for admin create
    useEffect(() => {
        if (!isStaff) return;
        (async () => {
            try {
                const [f, ca] = await Promise.all([
                    api.get("/flats"),
                    api.get("/complaints/common-areas"),
                ]);
                setFlats(f.data || []);
                setCommonAreas((ca.data && ca.data.common_areas) || []);
            } catch (e) { /* ignore */ }
        })();
    }, [isStaff]);

    // Reset form when opening/closing
    function openCreate() {
        setForm(emptyForm());
        setPendingPhotos([]);
        setCreateOpen(true);
    }
    function closeCreate() {
        // revoke object URLs
        pendingPhotos.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
        setPendingPhotos([]);
        setCreateOpen(false);
    }

    // Dashboard Quick Action deep-link: navigate here with state={{ autoOpenCreate: true }}
    useEffect(() => {
        if (location.state?.autoOpenCreate) {
            openCreate();
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Flat 360 Profile deep-link: navigate here with state={{ prefillSearch: "C-202" }}
    useEffect(() => {
        if (location.state?.prefillSearch) {
            setQ(location.state.prefillSearch);
            loadList(location.state.prefillSearch);
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Complaint Notifications V2 deep-link: notifications point here as /app/complaints?id=<cid>.
    // ComplaintDetail fetches full data itself from `.id` (via GET /complaints/{id}, which already
    // enforces the same RBAC as every other complaint access path — a notification never bypasses it).
    useEffect(() => {
        const openId = searchParams.get("id");
        if (openId) {
            setSelected({ id: openId });
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFilePick(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // allow re-picking
        const remaining = MAX_ATTACHMENTS - pendingPhotos.length;
        if (files.length === 0) return;
        if (remaining <= 0) { toast.error(`Maximum ${MAX_ATTACHMENTS} photos`); return; }
        const toAdd = files.slice(0, remaining).filter(f => {
            if (!ALLOWED_IMAGE_MIMES.includes(f.type)) {
                toast.error(`${f.name}: only JPG, PNG or WEBP allowed`); return false;
            }
            if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
                toast.error(`${f.name}: exceeds ${MAX_ATTACHMENT_MB}MB limit`); return false;
            }
            return true;
        });
        // Add optimistic entries, then upload each in parallel
        const seeds = toAdd.map(f => ({ file: f, previewUrl: URL.createObjectURL(f), uploading: true, file_id: null }));
        setPendingPhotos(prev => [...prev, ...seeds]);
        await Promise.all(seeds.map(async (seed) => {
            try {
                const fd = new FormData();
                fd.append("file", seed.file);
                const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setPendingPhotos(prev => prev.map(p => p === seed ? { ...p, uploading: false, file_id: data.id } : p));
            } catch (err) {
                toast.error(`${seed.file.name}: upload failed`);
                setPendingPhotos(prev => prev.filter(p => p !== seed));
                if (seed.previewUrl) URL.revokeObjectURL(seed.previewUrl);
            }
        }));
    }

    function removePendingPhoto(idx) {
        setPendingPhotos(prev => {
            const p = prev[idx];
            if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
            return prev.filter((_, i) => i !== idx);
        });
    }

    async function submitCreate(e) {
        e.preventDefault();
        // Verify all photos finished uploading
        if (pendingPhotos.some(p => p.uploading)) {
            toast.error("Please wait — photos are still uploading");
            return;
        }
        try {
            const payload = {
                subject: form.subject,
                description: form.description,
                category: form.category,
                priority: form.priority,
            };
            if (form.location) payload.location = form.location;
            const photoIds = pendingPhotos.map(p => p.file_id).filter(Boolean);
            if (photoIds.length) payload.photo_file_ids = photoIds;

            if (isStaff) {
                if (form.target_type === "RESIDENT") {
                    if (!form.target_flat_id) { toast.error("Please select a flat"); return; }
                    payload.target_type = "RESIDENT";
                    payload.target_flat_id = form.target_flat_id;
                } else if (form.target_type === "COMMON_AREA") {
                    if (!form.common_area) { toast.error("Please select a common area"); return; }
                    payload.target_type = "COMMON_AREA";
                    payload.common_area = form.common_area;
                    if (form.common_area_location) payload.common_area_location = form.common_area_location;
                }
            }
            // residents: server auto-derives target from their own flat

            await api.post("/complaints", payload);
            toast.success("Complaint raised");
            closeCreate();
            loadList();
        } catch (e) { toast.error(formatError(e)); }
    }

    // Filter flats by selected block for the admin flow
    const blocksList = useMemo(() => {
        const s = new Set();
        for (const f of flats) if (f.block) s.add(f.block);
        return Array.from(s).sort();
    }, [flats]);
    const flatsForBlock = useMemo(() => {
        if (!form.target_block_id) return [];
        return flats.filter(f => f.block === form.target_block_id).sort((a, b) => (a.number || "").localeCompare(b.number || ""));
    }, [flats, form.target_block_id]);
    const flatLabel = (f) => {
        if (!f) return "—";
        const num = f.label || f.flat_number || f.number || "";
        return f.block && num ? `${f.block}-${num}` : (num || f.block || "—");
    };
    const residentPreview = useMemo(() => {
        if (!form.target_flat_id) return null;
        const f = flats.find(x => x.id === form.target_flat_id);
        if (!f) return null;
        const first = (f.residents && f.residents[0]) || null;
        return first?.name || f.owner_name || "(No resident linked)";
    }, [flats, form.target_flat_id]);

    // Dup soft-warning within same resident
    const dupWarn = useMemo(() => {
        if (!form.subject || form.subject.length < 4) return null;
        if (isStaff) return null; // only warn residents
        const openStatuses = new Set(["new", "acknowledged", "assigned", "in_progress", "on_hold", "reopened"]);
        const q = form.subject.toLowerCase();
        const hit = complaints.find(c =>
            openStatuses.has(c.status) &&
            c.category === form.category &&
            (c.subject || "").toLowerCase().includes(q.slice(0, Math.min(q.length, 12)))
        );
        return hit ? hit : null;
    }, [form.subject, form.category, complaints, isStaff]);

    return (
        <div className="animate-fade-up space-y-6" data-testid="complaints-page">
            <PageHeader
                overline="Operations"
                title="Complaints & Service Requests"
                description={isStaff
                    ? "Manage resident requests end-to-end — categorize, assign, track and close."
                    : "Raise service requests for your flat or society and track resolution."}
                actions={
                    <Button data-testid="complaint-raise-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                        <Plus size={16} className="mr-1.5" /> Raise Request
                    </Button>
                }
            />

            {/* Summary — staff only */}
            {isStaff && summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="complaint-summary">
                    <StatCard label="Total Open" value={summary.total_open} accent testId="complaint-summary-total-open" />
                    <StatCard label="New" value={summary.by_status.new} testId="complaint-summary-new" />
                    <StatCard label="In Progress" value={summary.by_status.in_progress} testId="complaint-summary-in-progress" />
                    <StatCard label="On Hold" value={summary.by_status.on_hold} testId="complaint-summary-on-hold" />
                    <StatCard label="Resolved" value={summary.by_status.resolved} testId="complaint-summary-resolved" />
                    <StatCard label="High + Urgent" value={summary.high_urgent_open} accent testId="complaint-summary-high-urgent" />
                    <StatCard label="Open >7d" value={summary.open_over_7_days} testId="complaint-summary-over-7" />
                    <StatCard label="Open >15d" value={summary.open_over_15_days} accent testId="complaint-summary-over-15" />
                    <StatCard label="Closed" value={summary.by_status.closed} testId="complaint-summary-closed" />
                    <StatCard label="Reopened" value={summary.by_status.reopened} testId="complaint-summary-reopened" />
                    <StatCard label="Avg Resolution" value={summary.avg_resolution_hours + "h"} hint="Last 100" testId="complaint-summary-avg-res" />
                    <StatCard label="Total" value={summary.total} testId="complaint-summary-total" />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="complaint-filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="complaint-search" placeholder="Search by number, subject, description..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadList()} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="complaint-status-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="open_all">Open (all)</SelectItem>
                            {STATUSES.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger data-testid="complaint-category-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger data-testid="complaint-priority-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Priority" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All priorities</SelectItem>
                            {PRIORITIES.map(p => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {isStaff && (
                        <Select value={targetTypeFilter} onValueChange={setTargetTypeFilter}>
                            <SelectTrigger data-testid="complaint-target-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Raised For" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All (Resident + Common)</SelectItem>
                                <SelectItem value="resident">Resident</SelectItem>
                                <SelectItem value="common_area">Common Area</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                </div>
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="complaint-sort" className="rounded-sm border-brand-line w-52"><SelectValue placeholder="Sort" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="newest">Newest first</SelectItem>
                            <SelectItem value="oldest">Oldest first</SelectItem>
                            <SelectItem value="priority">Highest priority</SelectItem>
                            <SelectItem value="oldest_unresolved">Oldest unresolved</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={loadList} className="text-brand-ink"><MagnifyingGlass size={14} className="mr-1" /> Search</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatusFilter("all"); setCategoryFilter("all"); setPriorityFilter("all"); setTargetTypeFilter("all"); setSortBy("newest"); }} className="text-brand-inkSoft">Clear</Button>
                    </div>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <LoadingState message="Loading complaints..." />
            ) : complaints.length === 0 ? (
                <EmptyState title="Nothing here yet" description={isStaff ? "No complaints match your filters." : "You haven't raised any complaints yet. Tap Raise Request to get started."} />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="complaint-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[820px]">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Number</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Subject</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Raised For</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Category</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Priority</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Status</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Assigned</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Age</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Last update</th>
                                </tr>
                            </thead>
                            <tbody>
                                {complaints.map(c => (
                                    <tr key={c.id} data-testid={`complaint-row-${c.id}`} onClick={() => setSelected(c)} className="border-b border-brand-line/60 hover:bg-brand-bg/50 cursor-pointer">
                                        <td className="py-3 px-4 text-brand-ink font-mono text-xs">{c.complaint_number || "—"}</td>
                                        <td className="py-3 px-4 text-brand-ink font-medium">
                                            {c.subject || c.title}
                                            {isStaff && <p className="text-[10px] text-brand-inkSoft mt-0.5">by {c.created_by_name}</p>}
                                            {c.attachments && c.attachments.length > 0 && (
                                                <p className="text-[10px] text-brand-inkSoft mt-0.5 flex items-center gap-1"><ImageSquare size={11} weight="duotone" /> {c.attachments.length} photo{c.attachments.length > 1 ? "s" : ""}</p>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-xs" data-testid={`complaint-target-${c.id}`}>
                                            {c.target_type === "COMMON_AREA" ? (
                                                <>
                                                    <div className="flex items-center gap-1 text-brand-ink font-medium"><Buildings size={11} weight="duotone" /> {c.common_area || "Common Area"}</div>
                                                    {c.common_area_location && <p className="text-[10px] text-brand-inkSoft mt-0.5">{c.common_area_location}</p>}
                                                </>
                                            ) : c.target_type === "RESIDENT" ? (
                                                <>
                                                    <div className="flex items-center gap-1 text-brand-ink font-medium"><HouseLine size={11} weight="duotone" /> {c.target_flat_label || c.flat?.label || "—"}</div>
                                                    {c.target_resident_name && <p className="text-[10px] text-brand-inkSoft mt-0.5">{c.target_resident_name}</p>}
                                                </>
                                            ) : (
                                                <span className="text-brand-inkSoft italic">—</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{catLabel(c.category)}</td>
                                        <td className="py-3 px-4"><Chip variant={PRIORITY_VARIANT[c.priority] || "default"}>{statusLabel(c.priority)}</Chip></td>
                                        <td className="py-3 px-4"><Chip variant={STATUS_VARIANT[c.status] || "default"}>{statusLabel(c.status)}</Chip></td>
                                        <td className="py-3 px-4 text-brand-inkSoft text-xs">
                                            {c.assigned_staff ? (
                                                <><Wrench size={11} weight="duotone" className="inline mr-1" />{c.assigned_staff.name}</>
                                            ) : "—"}
                                        </td>
                                        <td className="py-3 px-4"><Chip variant={AGE_VARIANT[c.age_bucket] || "neutral"}>{c.age_days}d</Chip></td>
                                        <td className="py-3 px-4 text-brand-inkSoft text-xs">{fmt(c.updated_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={(v) => v ? openCreate() : closeCreate()}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">Raise a Service Request</DialogTitle>
                        <DialogDescription>Tell us what's happening — we'll route it to the right team.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-4">
                        {/* Raised For (staff only) */}
                        {isStaff && (
                            <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg" data-testid="complaint-raised-for">
                                <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Raised For *</Label>
                                <div className="flex gap-2">
                                    <Button type="button" onClick={() => setForm({ ...form, target_type: "RESIDENT" })}
                                        data-testid="complaint-target-resident"
                                        className={`flex-1 rounded-full ${form.target_type === "RESIDENT" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                                        <HouseLine size={14} className="mr-1" /> Resident
                                    </Button>
                                    <Button type="button" onClick={() => setForm({ ...form, target_type: "COMMON_AREA" })}
                                        data-testid="complaint-target-common-area"
                                        className={`flex-1 rounded-full ${form.target_type === "COMMON_AREA" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                                        <Buildings size={14} className="mr-1" /> Common Area
                                    </Button>
                                </div>

                                {form.target_type === "RESIDENT" && (
                                    <div className="grid md:grid-cols-3 gap-2 pt-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Block *</Label>
                                            <Select value={form.target_block_id} onValueChange={(v) => setForm({ ...form, target_block_id: v, target_flat_id: "" })}>
                                                <SelectTrigger data-testid="complaint-block-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Block" /></SelectTrigger>
                                                <SelectContent>{blocksList.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Flat *</Label>
                                            <Select value={form.target_flat_id} onValueChange={(v) => setForm({ ...form, target_flat_id: v })}>
                                                <SelectTrigger data-testid="complaint-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder={form.target_block_id ? "Pick flat" : "Pick block first"} /></SelectTrigger>
                                                <SelectContent>{flatsForBlock.map(f => <SelectItem key={f.id} value={f.id}>{flatLabel(f)}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Resident</Label>
                                            <Input data-testid="complaint-resident-preview" readOnly value={residentPreview || ""} placeholder="—" className="rounded-sm border-brand-line bg-white" />
                                        </div>
                                    </div>
                                )}

                                {form.target_type === "COMMON_AREA" && (
                                    <div className="grid md:grid-cols-2 gap-2 pt-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Common Area *</Label>
                                            <Select value={form.common_area} onValueChange={(v) => setForm({ ...form, common_area: v })}>
                                                <SelectTrigger data-testid="complaint-common-area-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick area" /></SelectTrigger>
                                                <SelectContent>{commonAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Location (optional)</Label>
                                            <Input data-testid="complaint-common-area-location" value={form.common_area_location} onChange={(e) => setForm({ ...form, common_area_location: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Tower B, 3rd Floor" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Resident header */}
                        {!isStaff && (
                            <div className="border border-brand-line rounded-sm p-3 bg-brand-bg text-xs" data-testid="complaint-resident-context">
                                <p className="uppercase tracking-overline text-brand-inkSoft">Raised For · Your Flat</p>
                                <p className="text-brand-ink font-medium mt-1">{user?.name}{user?.flat_id ? "" : " (no flat linked yet)"}</p>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label>Subject / Title *</Label>
                            <Input data-testid="complaint-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Water leakage in bathroom" />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Category *</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="complaint-category-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                    <SelectTrigger data-testid="complaint-priority-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(isStaff ? PRIORITIES : RESIDENT_PRIORITY_OPTIONS).map(p => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {!isStaff && <p className="text-[10px] text-brand-inkSoft">Committee decides "Urgent"</p>}
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Location (optional)</Label>
                            <Input data-testid="complaint-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Master bathroom, terrace, common area" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Description *</Label>
                            <Textarea data-testid="complaint-desc-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm border-brand-line" placeholder="Describe the issue with as much detail as possible..." />
                        </div>

                        {/* Photo attachments */}
                        <div className="space-y-2" data-testid="complaint-attachments">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-1"><Camera size={14} weight="duotone" /> Attachments (Photos)</Label>
                                <span className="text-[10px] text-brand-inkSoft">JPG/PNG/WEBP · up to {MAX_ATTACHMENTS} · max {MAX_ATTACHMENT_MB}MB each</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {pendingPhotos.map((p, idx) => (
                                    <div key={idx} className="relative border border-brand-line rounded-sm overflow-hidden group" data-testid={`complaint-photo-preview-${idx}`}>
                                        <img src={p.previewUrl} alt="" className="w-full h-24 object-cover" />
                                        {p.uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-[10px]">Uploading…</div>}
                                        {!p.uploading && <div className="absolute top-1 left-1 bg-white/90 text-brand-ink text-[9px] px-1.5 py-0.5 rounded-sm">Ready</div>}
                                        <button type="button" onClick={() => removePendingPhoto(idx)} aria-label="Remove"
                                            className="absolute top-1 right-1 bg-white/90 text-brand-action hover:bg-brand-action hover:text-white rounded-full p-1"
                                            data-testid={`complaint-photo-remove-${idx}`}>
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                ))}
                                {pendingPhotos.length < MAX_ATTACHMENTS && (
                                    <label className="border border-dashed border-brand-line rounded-sm h-24 flex flex-col items-center justify-center cursor-pointer hover:bg-brand-bg text-brand-inkSoft" data-testid="complaint-add-photo-btn">
                                        <Camera size={20} weight="duotone" />
                                        <span className="text-[10px] mt-1">Add Photo</span>
                                        <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFilePick} className="hidden" data-testid="complaint-photo-input" />
                                    </label>
                                )}
                            </div>
                        </div>

                        {dupWarn && (
                            <div className="bg-brand-action/10 border border-brand-action/40 rounded-sm p-3 text-xs text-brand-ink" data-testid="complaint-dup-warn">
                                <p className="font-medium flex items-center gap-1"><WarningCircle size={14} weight="duotone" /> You already have an open request in this category:</p>
                                <p className="mt-1">"{dupWarn.subject}" ({dupWarn.complaint_number}) — {statusLabel(dupWarn.status)}. You can still submit if this is a separate issue.</p>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={closeCreate} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="complaint-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Submit</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Complaint details</DialogTitle>
                        <DialogDescription>Full lifecycle, timeline and comments for the selected complaint.</DialogDescription>
                    </DialogHeader>
                    <button onClick={() => setSelected(null)} className="absolute right-4 top-4 text-brand-inkSoft hover:text-brand-ink" aria-label="Close"><X size={18} /></button>
                    {selected && (
                        <ComplaintDetail
                            complaint={selected}
                            isStaff={isStaff}
                            user={user}
                            staffList={staff}
                            onClose={() => setSelected(null)}
                            onChange={loadList}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
